import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import type { TokenManager } from "../../auth/token-manager";
import { capabilityForRequest, disableCapability } from "../capabilities";
import { CapabilityUnavailableError, SessionExpiredError } from "../errors";
import { isAbsoluteUrl } from "./is-absolute-url";

export interface StudioRetryOptions {
  /**
   * App-shell hook for the terminal "reopen the editor" state.
   *
   * Fires once per failed refresh WAVE: a single refresh() rejection expires
   * the leader and every queued follower with one shared SessionExpiredError,
   * and this callback runs exactly once for that wave — not once per request.
   *
   * However, it CAN fire once per request in a different terminal path: when
   * refresh() succeeds but the retried request still comes back 401 (e.g.
   * the server revoked the session between refresh and retry), each such
   * request independently hits its own "already retried" branch and calls
   * this hook on its own. The handler must therefore be idempotent — the app
   * shell, not this interceptor, owns the terminal latch (Plan 2C
   * requirement) so multiple calls collapse into one visible state change.
   */
  onSessionExpired?: (error: SessionExpiredError) => void;
}

type RetriableConfig = InternalAxiosRequestConfig & { _studioRetry?: boolean };

type QueueItem = {
  resolve: (token: string) => void;
  reject: (reason: unknown) => void;
};

/**
 * The dashboard retry.ts pattern — single-flight refresh, queued followers,
 * retry exactly once — with three deliberate differences:
 *
 * - state is PER INSTALL, not module-level: two instances (or two tests)
 *   must never share a refresh flight;
 * - the refresh source is manager.refresh() (a fresh host mint), and the
 *   token is never persisted anywhere;
 * - the terminal path is a typed SessionExpiredError + callback, NOT
 *   clearAuthAndRedirect(): the Studio has no localStorage tokens to clear
 *   and no login page to redirect to — inside an iframe that redirect would
 *   navigate to a route that does not exist.
 *
 * Also owns capability demotion: a 404/501 from a gated route proves the
 * allow-list optimistic, so the group turns off for the session and later
 * calls fail fast without network. A 500 never demotes — that is an outage
 * on a route that exists.
 */
export function installStudioRetry(
  instance: AxiosInstance,
  manager: TokenManager,
  options: StudioRetryOptions = {},
): number {
  let isRefreshing = false;
  let queue: QueueItem[] = [];

  const processQueue = (error: unknown, token: string | null) => {
    queue.forEach((item) => {
      if (error !== null) item.reject(error);
      else item.resolve(token as string);
    });
    queue = [];
  };

  const expire = (): SessionExpiredError => {
    const error = new SessionExpiredError();
    options.onSessionExpired?.(error);
    return error;
  };

  return instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config as RetriableConfig | undefined;

      // Third-party responses are not auth or capability signals: a 404 whose
      // path happens to collide with a gated route (e.g. an Azure host
      // serving "/context") must not demote it, and a 401 from a third party
      // must not trigger an editor-token refresh.
      if (original?.url && isAbsoluteUrl(original.url)) {
        return Promise.reject(error);
      }

      const status: number | undefined = error?.response?.status;

      // Capability demotion — checked first because a gated route answering
      // 404/501 is a routing fact, not an auth event.
      //
      // Except a 404 the route itself sends: the backend's envelope carries a
      // `code`, and only HTTP_ROUTE_NOT_FOUND means the route is missing. Any
      // other code is an answer about a record — /restore's "that restore
      // point no longer exists" — and demoting on it switched History and
      // Undo off for the rest of the session. A 404 with no envelope (an
      // older backend, a proxy) still counts as a missing route.
      const notFoundCode: unknown = error?.response?.data?.code;
      const isRecordNotFound =
        status === 404 &&
        typeof notFoundCode === "string" &&
        notFoundCode !== "HTTP_ROUTE_NOT_FOUND";
      if ((status === 404 || status === 501) && !isRecordNotFound && original?.url) {
        const capability = capabilityForRequest(original.method ?? "get", original.url);
        if (capability) {
          disableCapability(capability);
          return Promise.reject(new CapabilityUnavailableError(capability));
        }
      }

      if (status !== 401 || !original) {
        // 403 lands here on purpose: a valid token that is not allowed will
        // not become allowed by refreshing.
        return Promise.reject(error);
      }

      if (original._studioRetry) {
        // Retried with a fresh token and still 401 — terminal.
        return Promise.reject(expire());
      }

      if (isRefreshing) {
        // A refresh is already in flight — queue behind it and replay with
        // whatever token it produces.
        return new Promise<string>((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then((newToken) => {
          original._studioRetry = true;
          original.headers["Authorization"] = `Bearer ${newToken}`;
          return instance(original);
        });
      }

      original._studioRetry = true;
      isRefreshing = true;
      try {
        const fresh = await manager.refresh();
        processQueue(null, fresh.value);
        original.headers["Authorization"] = `Bearer ${fresh.value}`;
        return instance(original);
      } catch (refreshError) {
        // One typed error for the whole wave: the leader AND every queued
        // follower reject with the same SessionExpiredError, and
        // onSessionExpired fires exactly once. The raw refresh error is
        // deliberately not propagated — callers gate on the typed error.
        void refreshError;
        const expired = expire();
        processQueue(expired, null);
        return Promise.reject(expired);
      } finally {
        isRefreshing = false;
      }
    },
  );
}
